import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Tenant } from '@sw/tenant-types';
import { api } from './api-client';

interface TenantContextValue {
  tenant: Tenant | null;
  isLoading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  isLoading: true,
  error: null,
});

/**
 * Mount-time fetch de `GET /api/client/_meta/workspace` (SW-302 backend). El backend
 * resuelve workspace por Host header (subdomain {slug}.scoutingwizard.com o custom
 * domain verified). Si el host no resuelve, retorna 404 → frontend muestra error.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<Tenant>('/api/client/_meta/workspace')
      .then((data) => {
        if (!cancelled) {
          setTenant(data);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load tenant');
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <TenantContext.Provider value={{ tenant, isLoading, error }}>{children}</TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
